import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import Lightbox from '../components/Lightbox';
import { projectsData } from '../projectsData';
import './ProjectDetail.css';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';

export default function ProjectDetail({ t, language }) {
  const { id } = useParams();
  const location = useLocation();
  const project = projectsData.find(p => p.id === parseInt(id));
  
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  const projectImages = project?.images || [project?.image] || [];
  const projectDetails = t.projectDetails?.[parseInt(id)];
  const [visibleCount, setVisibleCount] = useState(3);

  const filterCategory = location.state?.filterCategory || 'all';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!project || !isAutoPlaying || projectImages.length <= visibleCount) return;
    
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % projectImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, projectImages.length, project, visibleCount]);

  if (!project) {
    return (
      <div className="project-detail-page">
        <div className="page-container">
          <p>{t.projectDetail.notFound}</p>
          <Link to="/projects">{t.projectDetail.backToProjects}</Link>
        </div>
      </div>
    );
  }

  const nextSlide = () => {
    setCarouselIndex((prev) => (prev + 1) % projectImages.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCarouselIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length);
    setIsAutoPlaying(false);
  };

  const openLightbox = (index) => {
    setLightboxStartIndex(index);
    setLightboxOpen(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getVisibleImages = () => {
    if (projectImages.length <= visibleCount) {
      return projectImages.map((src, index) => ({ src, index }));
    }
    
    const images = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (carouselIndex + i) % projectImages.length;
      images.push({ src: projectImages[index], index });
    }
    return images;
  };

  const visibleImages = getVisibleImages();

  return (
    <div className="project-detail-page">
      {/* SEO Component - unique per project */}
      <SEO
        title={`${project.title} - ${project.location} | Daniela Parito Architecture`}
        description={`${project.title} à ${project.location}. ${projectDetails?.subtitle || ''} Projet d'architecture par Daniela Parito, architecte à Montpellier.`}
        keywords={`${project.title}, architecte ${project.location}, ${project.category === 'collective' ? 'logement collectif' : project.category === 'individual' ? 'maison individuelle' : 'équipement public'} ${project.location}, architecture ${project.location}, DP Architecture`}
        image={`https://dp-architecture.com${project.image}`}
      />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'ArchitecturalProject',
        'name': project.title,
        'description': projectDetails?.subtitle || `Projet d'architecture à ${project.location}`,
        'location': {
          '@type': 'Place',
          'name': project.location
        },
        'image': `https://dp-architecture.com${project.image}`,
        'architect': {
          '@type': 'Architect',
          'name': 'Daniela Parito Architecture',
          'url': 'https://dp-architecture.com'
        }
      }} />

      <div className="page-container">
        <ScrollReveal>
          <Link 
            to="/projects" 
            state={{ filterCategory }}
            className="back-link"
          >
            <ChevronLeft size={20} />
            {t.projectDetail.backToProjects}
          </Link>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <h1 className="project-detail-title">{project.title}</h1>
          <p className="project-detail-location">{project.location}</p>
          {projectDetails?.subtitle && (
            <p className="project-detail-subtitle">{projectDetails.subtitle}</p>
          )}
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="carousel-multi">
            {projectImages.length > visibleCount && (
              <button onClick={prevSlide} className="carousel-nav carousel-nav-prev">
                <ChevronLeft size={24} />
              </button>
            )}

            <div className="carousel-images-grid">
              {visibleImages.map((img, idx) => (
                <div 
                  key={`${img.index}-${idx}`}
                  className="carousel-image-item"
                  onClick={() => openLightbox(img.index)}
                >
                  <img
                    src={img.src}
                    alt={`${project.title} - ${project.location} - Vue ${img.index + 1} | Architecte Montpellier`}
                    className="carousel-image"
                    /* ces images sont visibles sans scroll : les différer retardait
                       leur téléchargement de plusieurs secondes (LCP dégradé). */
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    fetchpriority={idx === 0 ? 'high' : undefined}
                    decoding="async"
                  />
                  <div className="carousel-image-overlay" />
                  <div className="carousel-image-hint">Cliquer pour agrandir</div>
                </div>
              ))}
            </div>

            {projectImages.length > visibleCount && (
              <button onClick={nextSlide} className="carousel-nav carousel-nav-next">
                <ChevronRight size={24} />
              </button>
            )}

            {projectImages.length > 1 && (
              <div className="carousel-counter">
                {carouselIndex + 1} / {projectImages.length}
              </div>
            )}
          </div>
        </ScrollReveal>

        <div className="description-grid">
          <div className="description-main">
            <ScrollReveal delay={0.3}>
              <h2 className="description-title">{t.projectDetail.description}</h2>
              <p className="description-text">
                {projectDetails?.fullDescription || 
                 project.fullDescription?.[language] || 
                 project.description?.[language] ||
                 t.projectDetail.noDescription}
              </p>
            </ScrollReveal>
          </div>

          {projectDetails?.details && (
            <div className="description-sidebar">
              <ScrollReveal delay={0.4}>
                <div className="details-box">
                  <h3 className="details-title">{t.projectDetail.informations}</h3>
                  <div className="details-list">
                    {projectDetails.details.mission && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.mission}</p>
                        <p className="detail-value">{projectDetails.details.mission}</p>
                      </div>
                    )}
                    {projectDetails.details.surface && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.surface}</p>
                        <p className="detail-value">{projectDetails.details.surface}</p>
                      </div>
                    )}
                    {projectDetails.details.cost && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.cost}</p>
                        <p className="detail-value">{projectDetails.details.cost}</p>
                      </div>
                    )}
                    {projectDetails.details.client && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.client}</p>
                        <p className="detail-value">{projectDetails.details.client}</p>
                      </div>
                    )}
                    {projectDetails.details.year && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.year}</p>
                        <p className="detail-value">{projectDetails.details.year}</p>
                      </div>
                    )}
                    {projectDetails.details.architecteAssocie && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.architecteAssocie}</p>
                        <p className="detail-value">{projectDetails.details.architecteAssocie}</p>
                      </div>
                    )}
                    {projectDetails.details.promoteur && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.promoteur}</p>
                        <p className="detail-value">{projectDetails.details.promoteur}</p>
                      </div>
                    )}
                    {projectDetails.details.bailleur && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.bailleur}</p>
                        <p className="detail-value">{projectDetails.details.bailleur}</p>
                      </div>
                    )}
                    {projectDetails.details.team && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.team}</p>
                        <div className="detail-value detail-value-multiline">
                          {Array.isArray(projectDetails.details.team) 
                            ? projectDetails.details.team.map((member, idx) => (
                                <p key={idx} className="team-member-line">{member}</p>
                              ))
                            : <p>{projectDetails.details.team}</p>
                          }
                        </div>
                      </div>
                    )}
                    {projectDetails.details.duration && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.duration}</p>
                        <p className="detail-value">{projectDetails.details.duration}</p>
                      </div>
                    )}
                    {projectDetails.details.performance && (
                      <div className="detail-item">
                        <p className="detail-label">{t.projectDetail.performance}</p>
                        <p className="detail-value">{projectDetails.details.performance}</p>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          )}
        </div>

        <ScrollReveal delay={0.5}>
          <div className="project-bottom-actions">
            <button 
              onClick={scrollToTop} 
              className="scroll-top-btn"
              aria-label="Retour en haut"
            >
              <ChevronUp size={20} />
              <span>Retour en haut</span>
            </button>
          </div>
        </ScrollReveal>
      </div>

      {lightboxOpen && projectImages.length > 0 && (
        <Lightbox
          images={projectImages}
          currentIndex={lightboxStartIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={() => setLightboxStartIndex((prev) => (prev + 1) % projectImages.length)}
          onPrev={() => setLightboxStartIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length)}
        />
      )}
    </div>
  );
}